import { useEffect, useMemo, useState } from 'react';
import { NavLink } from 'react-router-dom';
import { ComposerCanvas } from './components/ComposerCanvas';
import { ParameterInspector } from './components/ParameterInspector';
import { PrimitivePalette } from './components/PrimitivePalette';
import { WorkbenchSubroutePanel, type BenchSection } from './components/WorkbenchSubroutePanel';
import { LocalManifestSource, type ManifestSourceResult, type PrimitiveDef } from './manifest';
import {
  addPrimitiveToGraph,
  createGraphFromRecipe,
  LocalBenchRepository,
  runSubstrateComposition,
  updateNodeParameters,
  type AssetRecord,
  type BenchGraph,
  type PublishedRecord,
  type RecipeRecord,
  type RunRecord,
} from './runtime';

const SECTION_LINKS: Array<{ section: BenchSection; label: string; to: string }> = [
  { section: 'composer', label: 'Workbench', to: '/workbench' },
  { section: 'assets', label: 'Assets', to: '/workbench/assets' },
  { section: 'recipes', label: 'Recipes', to: '/workbench/recipes' },
  { section: 'runs', label: 'Runs', to: '/workbench/runs' },
  { section: 'published', label: 'Published', to: '/workbench/published' },
];

export function WorkbenchShell({ section }: { section: BenchSection }) {
  const source = useMemo(() => new LocalManifestSource(), []);
  const repository = useMemo(() => new LocalBenchRepository(), []);
  const [result, setResult] = useState<ManifestSourceResult | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [graph, setGraph] = useState<BenchGraph | null>(null);
  const [selectedNodeId, setSelectedNodeId] = useState<string | null>(null);
  const [recipes, setRecipes] = useState<RecipeRecord[]>([]);
  const [runs, setRuns] = useState<RunRecord[]>([]);
  const [assets, setAssets] = useState<AssetRecord[]>([]);
  const [published, setPublished] = useState<PublishedRecord[]>([]);
  const [currentRun, setCurrentRun] = useState<RunRecord | null>(null);

  useEffect(() => {
    let cancelled = false;
    source
      .load()
      .then((loaded) => {
        if (!cancelled) setResult(loaded);
      })
      .catch((err: unknown) => {
        if (!cancelled) setError(err instanceof Error ? err.message : 'Failed to load substrate manifest');
      });
    return () => {
      cancelled = true;
    };
  }, [source]);

  useEffect(() => {
    if (!result || graph) return;
    const loadedGraph = repository.loadGraph() ?? createGraphFromRecipe(result.manifest);
    setGraph(loadedGraph);
    setSelectedNodeId(loadedGraph.nodes[0]?.id ?? null);
    setRecipes(repository.listRecipes());
    setRuns(repository.listRuns());
    setAssets(repository.listAssets());
    setPublished(repository.listPublished());
  }, [graph, repository, result]);

  if (error) {
    return (
      <div style={{ padding: 24 }}>
        <div className="sp-empty" style={{ color: 'var(--sp-error)' }}>{error}</div>
      </div>
    );
  }

  if (!result) {
    return <div style={{ padding: 24, color: 'var(--sp-text-dim)' }}>Loading substrate manifest...</div>;
  }

  const { manifest } = result;
  const activeGraph = graph ?? createGraphFromRecipe(manifest);
  const selectedNode = activeGraph.nodes.find((node) => node.id === selectedNodeId) ?? activeGraph.nodes[0];
  const selectedPrimitive = selectedNode
    ? manifest.primitives.find((primitive) => primitive.id === selectedNode.primitiveId)
    : undefined;

  const persistGraph = (nextGraph: BenchGraph) => {
    const saved = repository.saveGraph(nextGraph);
    setGraph(saved);
    setSelectedNodeId((current) => current ?? saved.nodes[0]?.id ?? null);
  };

  const refreshRepositoryState = () => {
    setRecipes(repository.listRecipes());
    setRuns(repository.listRuns());
    setAssets(repository.listAssets());
    setPublished(repository.listPublished());
  };

  const handleAddPrimitive = (primitive: PrimitiveDef) => {
    const nextGraph = addPrimitiveToGraph(activeGraph, primitive);
    persistGraph(nextGraph);
    setSelectedNodeId(nextGraph.nodes[nextGraph.nodes.length - 1]?.id ?? null);
  };

  const handleParameterChange = (parameters: Record<string, unknown>) => {
    if (!selectedNode) return;
    persistGraph(updateNodeParameters(activeGraph, selectedNode.id, parameters));
  };

  const handleSaveRecipe = () => {
    repository.saveRecipe(activeGraph);
    refreshRepositoryState();
  };

  const handleResetRecipe = () => {
    const nextGraph = createGraphFromRecipe(manifest);
    persistGraph(nextGraph);
    setSelectedNodeId(nextGraph.nodes[0]?.id ?? null);
    setCurrentRun(null);
  };

  const persistRunResult = (decision?: 'approve' | 'reject' | 'modify') => {
    const result = runSubstrateComposition({ manifest, graph: activeGraph, approvalDecision: decision });
    repository.saveRun(result.run);
    repository.saveAssets(result.assets);
    repository.savePublished(result.published);
    setCurrentRun(result.run);
    refreshRepositoryState();
    if (result.run.pausedAtNodeId) setSelectedNodeId(result.run.pausedAtNodeId);
  };

  return (
    <div style={{ display: 'grid', gap: 16, padding: 16 }}>
      <header
        className="sp-widget"
        style={{
          display: 'grid',
          gap: 12,
          height: 'auto',
          overflow: 'visible',
        }}
      >
        <div style={{ alignItems: 'start', display: 'flex', justifyContent: 'space-between', gap: 16 }}>
          <div>
            <p className="mono" style={{ color: 'var(--sp-accent)', fontSize: 12, letterSpacing: '0.08em', margin: 0 }}>
              PLUS WORKBENCH V1 / LOCAL COMPOSER
            </p>
            <h1 style={{ color: 'var(--sp-text)', fontSize: 24, margin: '6px 0' }}>Substrate Composer</h1>
            <p style={{ color: 'var(--sp-text-muted)', maxWidth: 880, margin: 0 }}>
              A power-user canvas for composing substrate primitives. This surface is substrate-only: no vertical
              switcher, no Sakme room metaphor, no product-specific tool catalog.
            </p>
          </div>
          <div className="mono" style={{ color: 'var(--sp-text-dim)', fontSize: 11, textAlign: 'right' }}>
            <div>source={result.source}</div>
            <div>manifest={manifest.version}</div>
          </div>
        </div>

        <nav style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
          {SECTION_LINKS.map((link) => (
            <NavLink
              key={link.section}
              to={link.to}
              end={link.section === 'composer'}
              className="sp-btn"
              style={({ isActive }) => ({
                background: isActive ? 'var(--sp-accent-strong)' : 'var(--sp-bg-elev-2)',
                borderColor: isActive ? 'var(--sp-accent-strong)' : 'var(--sp-border-strong)',
                color: isActive ? 'var(--sp-bg)' : 'var(--sp-text)',
                textDecoration: 'none',
              })}
            >
              {link.label}
            </NavLink>
          ))}
        </nav>
      </header>

      <div style={{ display: 'grid', gap: 16, gridTemplateColumns: 'minmax(280px, 340px) minmax(0, 1fr)' }}>
        <PrimitivePalette manifest={manifest} onAddPrimitive={handleAddPrimitive} />
        {section === 'composer' ? (
          <div style={{ display: 'grid', gap: 16, gridTemplateColumns: 'minmax(0, 1fr) minmax(260px, 320px)' }}>
            <ComposerCanvas
              currentRun={currentRun}
              graph={activeGraph}
              manifest={manifest}
              onResetRecipe={handleResetRecipe}
              onRun={() => persistRunResult()}
              onSaveRecipe={handleSaveRecipe}
              onSelectNode={setSelectedNodeId}
              selectedNodeId={selectedNodeId}
            />
            <ParameterInspector
              currentRun={currentRun}
              node={selectedNode}
              onApprove={() => persistRunResult('approve')}
              onChange={handleParameterChange}
              onReject={() => persistRunResult('reject')}
              primitive={selectedPrimitive}
            />
          </div>
        ) : (
          <WorkbenchSubroutePanel
            assets={assets}
            manifest={manifest}
            published={published}
            recipes={recipes}
            runs={runs}
            section={section}
          />
        )}
      </div>
    </div>
  );
}
